import React, { useState } from "react";
import {
  Typography,
  Button,
  TextField,
  Chip,
  CircularProgress,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";

const AIContentOptimizer = () => {
  const [isOptimizing, setIsOptimizing] = useState(false);
  const [originalCaption, setOriginalCaption] = useState("");
  const [optimizedCaption, setOptimizedCaption] = useState("");
  const [engagementScore, setEngagementScore] = useState(0);
  const [aiFeatures] = useState([
    "Hashtag Optimization",
    "Engagement Prediction",
    "Trend Analysis",
    "Brand Voice Enhancement",
    "A/B Testing Suggestions",
  ]);

  const optimizeContent = async () => {
    if (!originalCaption.trim()) return;

    setIsOptimizing(true);
    // Simulate AI optimization
    setTimeout(() => {
      const optimized = `${originalCaption}\n\n#engagement #growth #viral #trending #instagram #socialmedia #digitalmarketing #contentcreator #influencer #brand`;
      setOptimizedCaption(optimized);
      setEngagementScore(Math.floor(Math.random() * 40) + 60); // 60-100%
      setIsOptimizing(false);
    }, 3000);
  };

  return (
    <div>
      <Typography variant="body2" className="text-gray-600 dark:text-gray-400 mb-6">
        Our AI analyzes your content and suggests optimizations for maximum
        engagement and reach.
      </Typography>

      <div className="mb-6">
        <Typography variant="subtitle2" className="text-sm text-gray-600 dark:text-gray-400 mb-3">
          AI Features:
        </Typography>
        <div className="flex flex-wrap gap-2">
          {aiFeatures.map((feature, index) => (
            <Chip
              key={index}
              label={feature}
              size="small"
              color="primary"
              variant="outlined"
              icon={<TrendingUpIcon />}
            />
          ))}
        </div>
      </div>

      <div className="mb-6">
        <Typography variant="subtitle2" className="text-sm text-gray-600 dark:text-gray-400 mb-2">
          Original Caption:
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={4}
          value={originalCaption}
          onChange={(e) => setOriginalCaption(e.target.value)}
          placeholder="Enter your Instagram caption here..."
          variant="outlined"
          className="bg-white dark:bg-gray-800 mb-4"
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '0.75rem',
            },
          }}
        />

        <Button
          variant="contained"
          onClick={optimizeContent}
          disabled={isOptimizing || !originalCaption.trim()}
          startIcon={
            isOptimizing ? (
              <CircularProgress size={20} />
            ) : (
              <AutoAwesomeIcon />
            )
          }
          className="bg-primary-600 hover:bg-primary-700 text-white font-semibold px-6 py-2.5 rounded-xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 normal-case"
        >
          {isOptimizing ? "Optimizing..." : "Optimize with AI"}
        </Button>
      </div>

      {optimizedCaption && (
        <div>
          <Typography variant="subtitle2" className="text-sm text-gray-600 dark:text-gray-400 mb-2">
            AI Optimized Caption:
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            value={optimizedCaption}
            variant="outlined"
            className="bg-white dark:bg-gray-800 mb-4"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '0.75rem',
              },
            }}
          />

          <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl mb-4">
            <Typography variant="body2" className="text-green-800 dark:text-green-200">
              <strong>Predicted Engagement:</strong> {engagementScore}%
              increase
            </Typography>
          </div>

          <Button
            variant="outlined"
            onClick={() => navigator.clipboard.writeText(optimizedCaption)}
            startIcon={<AutoAwesomeIcon />}
            className="border-primary-600 text-primary-600 hover:bg-primary-50 dark:border-primary-400 dark:text-primary-400 dark:hover:bg-primary-900/20 normal-case"
          >
            Copy Optimized Caption
          </Button>
        </div>
      )}
    </div>
  );
};

export default AIContentOptimizer;
